import { useState, useEffect } from "react";
import {
  Search,
  Package,
  AlertTriangle,
  CheckCircle,
  Edit,
} from "lucide-react";

import { GetallProduct } from "../api";

function Inventory() {
  const [search, setSearch] = useState("");
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = async () => {
    try {
      const response = await GetallProduct();

      console.log("Products:", response.data);

      if (response.data.success) {
        setInventory(response.data.products);
      }
    } catch (error) {
      console.log("Error Fetching Products:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredInventory = inventory.filter((item) =>
    item.title?.toLowerCase().includes(search.toLowerCase()),
  );

  const totalProducts = inventory.length;

  const inStock = inventory.filter((item) => item.stock > 10).length;

  const lowStock = inventory.filter((item) => item.stock <= 10).length;

  return (
    <div className="space-y-6 p-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Total Products */}
        <div className="bg-white rounded-xl shadow p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Products</p>
              <h2 className="text-2xl sm:text-3xl font-bold mt-1">
                {totalProducts}
              </h2>
            </div>

            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* In Stock */}
        <div className="bg-white rounded-xl shadow p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Stock</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-green-600 mt-1">
                {inStock}
              </h2>
            </div>

            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Low Stock */}
        <div className="bg-white rounded-xl shadow p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Low Stock</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-red-600 mt-1">
                {lowStock}
              </h2>
            </div>

            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Inventory */}
      <div className="bg-white rounded-2xl shadow p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold">
              Inventory Management
            </h2>

            <p className="text-sm sm:text-base text-gray-500">
              Manage Hydroponic Product Stock
            </p>
          </div>

          <div className="relative w-full md:w-80">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              placeholder="Search Product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border rounded-xl pl-11 pr-4 py-3"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading Products...</div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-4 text-left">Product</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Available</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredInventory.map((item) => (
                    <tr key={item._id} className="border-b hover:bg-gray-50">
                      <td className="p-4 font-medium">{item.title}</td>

                      <td>SKU-{item._id.slice(-6)}</td>

                      <td>{item.category}</td>

                      <td>{item.stock}</td>

                      <td>
                        {item.stock > 10 ? (
                          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                            In Stock
                          </span>
                        ) : item.stock > 0 ? (
                          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm">
                            Low Stock
                          </span>
                        ) : (
                          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                            Out of Stock
                          </span>
                        )}
                      </td>

                      <td>
                        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                          <Edit size={16} />
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-4">
              {filteredInventory.map((item) => (
                <div key={item._id} className="border rounded-xl p-4 shadow-sm">
                  <h3 className="font-semibold text-lg">{item.title}</h3>

                  <p className="text-sm text-gray-500">{item.category}</p>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-gray-500">SKU</p>
                      <p>SKU-{item._id.slice(-6)}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">Stock</p>
                      <p>{item.stock}</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    {item.stock > 10 ? (
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                        In Stock
                      </span>
                    ) : item.stock > 0 ? (
                      <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm">
                        Low Stock
                      </span>
                    ) : (
                      <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                        Out of Stock
                      </span>
                    )}
                  </div>

                  <div className="flex">
                    {" "}
                    <button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex justify-center items-center gap-2">
                      <Edit size={16} />
                      Update
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && filteredInventory.length === 0 && (
          <div className="text-center py-10 text-gray-500">
            No Products Found
          </div>
        )}
      </div>
    </div>
  );
}

export default Inventory;
